import { assert } from '../../assert/assert.js'
import { NumberLike } from '../../math/bigNumber.js'
import { durationLikeToMs } from '../duration/durationLikeToMs.js'
import { DurationLike, UnixDuration } from '../duration/UnixDuration.js'
import { timestampLikeToMs } from './timestampLikeToMs.js'

export interface UnixTimestamp {
  plus(duration: DurationLike): UnixTimestamp
  minus(duration: DurationLike): UnixTimestamp
  since(other: TimestampLike): UnixDuration
  until(other: TimestampLike): UnixDuration

  gt(value: TimestampLike): boolean
  gte(value: TimestampLike): boolean
  lt(value: TimestampLike): boolean
  lte(value: TimestampLike): boolean
  eq(value: TimestampLike): boolean

  toDate(): Date
  toMilliseconds(): bigint
  toSeconds(): bigint
}

type UnixTimestampParam = { seconds: NumberLike } | { milliseconds: NumberLike }

class UnixTimestampClass implements UnixTimestamp {
  private readonly milliseconds: bigint

  constructor(value: UnixTimestampParam) {
    this.milliseconds = timestampLikeToMs(value)
  }

  plus(duration: DurationLike): UnixTimestamp {
    const durationMs = durationLikeToMs(duration)
    return UnixTimestamp({ milliseconds: this.toMilliseconds() + durationMs })
  }

  minus(duration: DurationLike): UnixTimestamp {
    const durationMs = durationLikeToMs(duration)
    const result = this.toMilliseconds() - durationMs
    assert(
      result >= 0,
      `Cannot sub duration (${durationMs}ms) to UnixTimestamp (${this.toMilliseconds()}ms) - result below zero`,
    )
    return UnixTimestamp({ milliseconds: result })
  }

  since(other: TimestampLike): UnixDuration {
    return UnixDuration({ milliseconds: Number(this.toMilliseconds() - timestampLikeToMs(other)) })
  }

  until(other: TimestampLike): UnixDuration {
    return UnixDuration({ milliseconds: Number(timestampLikeToMs(other) - this.toMilliseconds()) })
  }

  gt(value: TimestampLike): boolean {
    return this.milliseconds > timestampLikeToMs(value)
  }

  gte(value: TimestampLike): boolean {
    return this.milliseconds >= timestampLikeToMs(value)
  }

  lt(value: TimestampLike): boolean {
    return this.milliseconds < timestampLikeToMs(value)
  }

  lte(value: TimestampLike): boolean {
    return this.milliseconds <= timestampLikeToMs(value)
  }

  eq(value: TimestampLike): boolean {
    return this.milliseconds === timestampLikeToMs(value)
  }

  toSeconds(): bigint {
    return this.milliseconds / 1000n
  }

  toMilliseconds(): bigint {
    return this.milliseconds
  }

  toDate(): Date {
    return new Date(Number(this.toMilliseconds()))
  }

  static now(): UnixTimestamp {
    return UnixTimestamp.fromDate(new Date())
  }

  static fromDate(date: Date): UnixTimestamp {
    return UnixTimestamp({ milliseconds: date.getTime() })
  }

  static fromString(dateString: string): UnixTimestamp {
    return UnixTimestamp.fromDate(new Date(dateString))
  }

  static fromTimestampLike(value: TimestampLike): UnixTimestamp {
    return UnixTimestamp({ milliseconds: timestampLikeToMs(value) })
  }

  static isInstance(value: unknown): value is UnixTimestamp {
    return value instanceof UnixTimestampClass
  }
}

function UnixTimestampFunction(value: UnixTimestampParam): UnixTimestampClass {
  return new UnixTimestampClass(value)
}

interface StaticUnixTimestamp {
  new (value: UnixTimestampParam): UnixTimestamp
  (value: UnixTimestampParam): UnixTimestamp
  readonly prototype: UnixTimestamp

  now(): UnixTimestamp
  fromDate(date: Date): UnixTimestamp
  fromString(dateString: string): UnixTimestamp
  fromTimestampLike(value: TimestampLike): UnixTimestamp
  isInstance(value: unknown): value is UnixTimestamp
}

Object.setPrototypeOf(UnixTimestampFunction, UnixTimestampClass)
UnixTimestampFunction.prototype = UnixTimestampClass.prototype

export const UnixTimestamp: StaticUnixTimestamp = Object.assign(UnixTimestampFunction, UnixTimestampClass)

export type TimestampLike = UnixTimestampParam | UnixTimestamp
